.pledges {
  position: relative;
  z-index: 1;
  /* Edge + chrome clearance is handled by the live stage safe-area
   * (--token-safe-*); the page fills its safe box. */
  padding: 0;
  font-family: var(--token-font-ui);
  color: var(--token-text-primary);
  /* Fill the KioskStage height so vertical centering actually has
   * room to work (100vh ties to the real viewport which is shorter
   * than the 1080 design when letterboxed). */
  flex: 1;
  min-height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: var(--token-space-xl);
}

.empty {
  position: relative;
  z-index: 1;
  padding: var(--token-space-2xl);
  font-family: var(--token-font-ui);
}

.header {
  display: flex;
  gap: var(--token-space-md);
  margin-bottom: var(--token-space-xl);
}

.navButton {
  min-height: var(--token-touch-min);
  padding: var(--token-space-sm) var(--token-space-lg);
  border-radius: var(--token-radius-pill);
  border: 1px solid rgba(255, 255, 255, 0.45);
  background: rgba(255, 255, 255, 0.35);
  color: var(--token-text-primary);
  backdrop-filter: blur(18px) saturate(160%);
  -webkit-backdrop-filter: blur(18px) saturate(160%);
  font-family: inherit;
  cursor: pointer;
}

/* Positioning context for the card + its prev/next arrows, capped to the
 * card's max width so the arrows sit on the card edges (not the page edges). */
.stage {
  position: relative;
  flex: 1;
  min-height: 0;
  width: 100%;
  max-width: 1800px;
  display: flex;
}

.card {
  /* The card FILLS its safe box (one stable size across pledges, no
   * visible resize as Sarah taps) and is a size container, so the title
   * + body scale to fit it via cqh units — the whole pledge always
   * shows, never clipped, at every viewport from laptop to 4K. The old
   * fixed min-height:860 + overflow:hidden clipped the title + last line
   * once the 2x font sizes outgrew the box.
   * `position: relative` + `overflow: hidden` still clips the optional
   * bg image to the rounded corners. */
  position: relative;
  overflow: hidden;
  background: var(--token-card-bg);
  border: 1.5px solid var(--token-accent);
  border-radius: var(--token-radius-card);
  box-shadow: var(--token-shadow-card);
  width: 100%;
  flex: 1;
  min-height: 0;
  container-type: size;
  padding: clamp(28px, 7cqh, 120px) clamp(48px, 8cqi, 200px);
  cursor: pointer;
  font-family: inherit;
  text-align: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: clamp(12px, 2.5cqh, 40px);
}

/* Background artwork — sits behind the title + body at 50% opacity so
 * the pledge wording stays easy to read. Cover-fit so the image fills
 * the card regardless of aspect ratio differences between the
 * artwork (≈ 2:1) and the card (varies with pledge length). */
.cardBg {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: 0.5;
  pointer-events: none;
  user-select: none;
  z-index: 0;
}

/* Lift the text above the background image. */
.cardTitle,
.cardBody {
  position: relative;
  z-index: 1;
}

.cardTitle {
  font-family: var(--token-font-display);
  /* Scales to the card height (cqh) so it stays big for readability but
   * never pushes the body out of the card. Bumped up to fill the card more. */
  font-size: clamp(48px, 16cqh, 190px);
  line-height: 1.1;
  margin: 0;
}

.cardBody {
  font-family: var(--token-font-display);
  /* Sized so the longest pledge (the 6-line Bible pledge) still fits the card
   * at any viewport, but as large as possible so the text fills the card. */
  font-size: clamp(28px, 9cqh, 90px);
  line-height: 1.4;
  white-space: pre-line;
  margin: 0;
}

/* Glass prev/next arrows on the card's left + right edges. Only rendered when
 * more than one pledge is enabled, to show the card can be scrolled. Sized off
 * --ui-scale like the rest of the live UI. */
.arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 3;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  width: calc(64px * var(--ui-scale));
  height: calc(64px * var(--ui-scale));
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.6);
  background: rgba(255, 255, 255, 0.34);
  backdrop-filter: blur(18px) saturate(160%);
  -webkit-backdrop-filter: blur(18px) saturate(160%);
  box-shadow:
    0 10px 24px -8px rgba(8, 18, 12, 0.4),
    inset 0 1px 0 rgba(255, 255, 255, 0.6);
  color: var(--token-text-primary);
  cursor: pointer;
  transition: background 120ms ease;
}

.arrow:hover {
  background: rgba(255, 255, 255, 0.55);
}

.arrowLeft {
  left: calc(20px * var(--ui-scale));
}

.arrowRight {
  right: calc(20px * var(--ui-scale));
}

.arrow svg {
  width: 56%;
  height: 56%;
}

.dots {
  display: flex;
  justify-content: center;
  gap: var(--token-space-md);
}

.dot {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.4);
  border: 1px solid rgba(255, 255, 255, 0.6);
}

.dot[data-active='true'] {
  background: var(--token-accent);
  border-color: var(--token-accent);
}
